// Save-format version + the per-entity script-memory codec (ENG-11; ARCH §27.7,
// SCR-8). PURE (ENG-1) — config is the only import, so both the browser save path
// (`src/client/storage/**`) and a headless host can share the one number.
//
// ENG-11 froze the save format at M25: every change after the freeze ships a version
// bump, a migration, and a committed fixture proving a pre-migration save still
// opens. `SAVE_VERSION` here is the version-of-record for that chain — the post-freeze
// sequence is 2 (Part VII / TAL-4) → 3 (Part VIII / M45.2) → 4 (Part IX / M48.6) →
// 5 (Part X / M51.5) → **6 (Part XI / M55.5)**. `src/client/storage/migrate.ts`
// re-exports it as `CURRENT_SAVE_VERSION` so exactly one number exists in the tree.
//
// What v6 adds (§30.7): `WorldMeta.adultsOnly?` (owner-set, COW-10 — the shared-
// screen policy gate) and the three new block entities' data (`sticky_note`/
// `task_board`/`timer`, M55.1) — the SAME additive-only discipline every prior
// bump used: absent on every pre-v6 save (no `adultsOnly` field, no rows of those
// three block-entity types at all), which is the whole migration. The server's
// sign side table and presence status persist NOTHING (session-scoped, §30.2/
// §30.4) — neither touches the save format at all.
//
// What v5 adds (§29.13): `WorldMeta.lesson?`/`lessonLog?` and `PlayerState.lessons?`
// — a world's single active lesson, its instructor-console log, and each player's
// per-lesson results (`sim/education/results.ts`). Same additive-only discipline:
// absent on every pre-v5 save, exactly like `notebook?` before it (v4).
//
// What v3 adds is `Entity.scriptMem` (§27.7): a bag of its OWN, never `Entity.data`
// (that is engine scratch the archetypes and the renderer read, so a script writing
// into it would be an injection channel into engine state). It saves with the entity
// and NEVER replicates — the server owns it and no client needs it to render a pose —
// so the caps below bound save size, not bandwidth.
//
// **The migration is the absence of work.** A save written before the field existed
// simply has no `scriptMem` key, and `decodeScriptMem(undefined)` is an empty bag: the
// same additive-only discipline `migrate.ts` runs on world/player rows (fill what a
// newer format introduced, never drop what you do not recognise). A memory-free entity
// also encodes with no key at all, so an unscripted mob's row stays byte-identical to
// a pre-M45 one — the parity M45.7 re-proves. `tests/fixtures/save-pre-scriptmem.json`
// is the committed v2 fixture that pins both halves.
import { SCR } from '../config';

/** The current save-format version (ENG-11). Bumping it requires a migration step +
 *  a committed pre-migration fixture; see the chain in this file's header. */
export const SAVE_VERSION = 7;

/** The version a save written before `task_board` lines carried a stable `id` (v6).
 *  v7 adds `TaskLine.id` (ARCH §39.7, M107.2) — a real migration, not mere absence
 *  tolerance: `BlockEntityStore.loadJSON` assigns every id-less line one deterministically
 *  from its position (`stableLineId`, `src/sim/coworking/text.ts`), so the same un-migrated
 *  row always lands on the same ids. Nothing else on a v6 save changes. */
export const SAVE_VERSION_PRE_BOARD_LINK = 6;

/** The version a save written before Part XI's co-working fields carries (v5). v6
 *  adds — all optional/absent unless used — `WorldMeta.adultsOnly` and the three
 *  new block-entity types (§30.7, M55.5). */
export const SAVE_VERSION_PRE_COWORKING = 5;

/** The version a save written before Part X's lesson fields carries (v4). v5 adds —
 *  all optional, all absent unless a lesson was ever active/played — `WorldMeta.
 *  lesson`/`lessonLog` and `PlayerState.lessons` (§29.13, M51.5). */
export const SAVE_VERSION_PRE_EDU = 4;

/** The version a save written before Part IX's heredity fields carries (v3). v4 adds —
 *  all optional, all absent unless used — `SavedEntity.genome`/`parents`/`name`/`id`, the
 *  `crop` block entity and `PlayerState.notebook` (ARCH §28.12, M48.6). A v3 save simply
 *  has none of them, which is the whole migration. */
export const SAVE_VERSION_PRE_HEREDITY = 3;

/** The version a save written *before* `scriptMem` existed carries (Part VII / v2).
 *  The ENG-11 fixture is stamped with this, and loading it must not error. */
export const SAVE_VERSION_PRE_SCRIPTMEM = 2;

/** What a script may store: the language's primitives only. A vec3 (and a list) is a
 *  value of the language but NOT of memory — memory is for state machines, not
 *  geometry, so a position is stored as three numbers (§27.7). */
export type ScriptMemValue = number | string | boolean;

/** One entity's memory bag: ≤ `SCR.MEM.KEYS` identifier-shaped keys (§27.7). */
export type ScriptMem = Record<string, ScriptMemValue>;

/** Memory keys match the script language's identifier pattern (§27.7). The lexer's
 *  `isIdentStart`/`isIdentPart` rule is private to `script/lang/lex.ts`; this is the
 *  same grammar as a regex, so a key is always a name a script could have written. */
const SCRIPT_MEM_KEY = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** True iff `key` is a storable memory key (identifier-shaped, §27.7). */
export function isScriptMemKey(key: string): boolean {
  return SCRIPT_MEM_KEY.test(key);
}

/** True iff `v` is a storable memory value — number/string/bool within
 *  `SCR.MEM.STR_MAX`. A vec3, a list, null and undefined are all rejected. */
export function isScriptMemValue(v: unknown): v is ScriptMemValue {
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v === 'string') return v.length <= SCR.MEM.STR_MAX;
  return typeof v === 'boolean';
}

/**
 * Rebuild a memory bag from untrusted input, keeping only entries that satisfy every
 * §27.7 cap and stopping at `SCR.MEM.KEYS`. An imported or hand-edited save is
 * untrusted input, so this NEVER throws (E-33): a bad entry is dropped, not crashed
 * on, and a bag that loses every entry comes back `undefined` — indistinguishable
 * from the pre-migration absence, which is exactly the ENG-11 behaviour we want.
 * Insertion order is preserved, so encode → decode is an exact round-trip.
 */
function sanitizeScriptMem(raw: unknown): ScriptMem | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const out: ScriptMem = {};
  let n = 0;
  for (const [key, value] of Object.entries(raw)) {
    if (n >= SCR.MEM.KEYS) break;
    if (!isScriptMemKey(key) || !isScriptMemValue(value)) continue;
    out[key] = value;
    n++;
  }
  return n === 0 ? undefined : out;
}

/** Encode an entity's memory for its save row: a defensive copy (never an alias into
 *  live entity state), or `undefined` for an entity that never stored anything — so
 *  the key is simply absent, exactly as in every pre-v3 save. */
export function encodeScriptMem(mem: ScriptMem | undefined): ScriptMem | undefined {
  return mem === undefined ? undefined : sanitizeScriptMem(mem);
}

/** Decode a save row's memory. **The ENG-11 migration:** a pre-v3 row has no
 *  `scriptMem` key at all, so `raw` is `undefined` and the entity loads with empty
 *  memory and no error. */
export function decodeScriptMem(raw: unknown): ScriptMem | undefined {
  return raw === undefined ? undefined : sanitizeScriptMem(raw);
}

// ── Per-instance script override (ARCH §27.13, SCR-18) ────────────────────────
// `Entity.scriptOverride` names the script ONE entity runs instead of its type's. It
// rides the same v3 bump as `scriptMem` (§27.13: "the ENG-11 migration of §27.7
// covers it"), and for the same reason needs no work of its own: a row written before
// the field existed has no key, and absent is "run the type's script".

/** A script NAME, by the lexer's identifier rule — the same grammar `MobDef.script` is
 *  bound to (M45.1), so an override can name exactly what a type can. */
const SCRIPT_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** True iff `name` could name a script (identifier-shaped). */
export function isScriptName(name: unknown): name is string {
  return typeof name === 'string' && SCRIPT_NAME.test(name);
}

/** Decode a save row's override from untrusted input (E-33): an identifier is kept,
 *  anything else — including absence, the pre-M47 case — is "no override", never an
 *  error. Whether the name still RESOLVES is not a save-format question: a dangling
 *  override loads fine and falls back at dispatch (E-50). */
export function decodeScriptOverride(raw: unknown): string | undefined {
  return isScriptName(raw) ? raw : undefined;
}

// ── Heredity fields (ARCH §28.9/§28.12, save v4) ──────────────────────────────

/** Longest animal name a name tag writes (§28.9 — "max 24 chars"). */
export const ENTITY_NAME_MAX = 24;
/** Printable ASCII and space — the name tag prompt's alphabet (§28.9). */
const ENTITY_NAME = /^[\x20-\x7e]+$/;

/** A saved `Entity.name`, or undefined for anything a name tag could not have written
 *  (E-33: a save is untrusted input). Whitespace-only is not a name. */
export function decodeEntityName(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || raw.length > ENTITY_NAME_MAX || !ENTITY_NAME.test(raw)) return undefined;
  return raw.trim() === '' ? undefined : raw;
}
